import { JSX } from "react";
import {
    ProductGridItem,
    ProductListItem,
} from "@hatch-digital-nl/bravilor-storybook";
import { ProductIndexItemInterface } from "../../../common/interfaces/apiV2/shared/ProductIndexItemInterface";
import { StorybookSimpleProduct } from "../../../common/types/storybook";
import {
    convertProductToGridFormat,
    convertProductToListFormat,
    SimpleProduct,
} from "../../../common/utils/productConverters";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import { CategoryProductsSkeleton } from "./CategoryProductsSkeleton";
import { LanguageInterface } from "../../../common/interfaces/shared/LanguageInterface";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";

interface CategoryProductListProps {
    products: ProductIndexItemInterface[];
    gridLayout: boolean;
    isLoading: boolean;
    disableCheckbox: boolean;
    selectedProducts: ProductIndexItemInterface[];
    activeLanguage: LanguageInterface | null;
    onProductSelect: (product: SimpleProduct) => void;
    onProductClick: (product: SimpleProduct) => void;
}

const isProductSelected = (
    selectedProducts: ProductIndexItemInterface[],
    product: ProductIndexItemInterface,
): boolean => selectedProducts.some((item) => item.id === product.id);

export const CategoryProductList = (
    props: CategoryProductListProps,
): JSX.Element | null => {
    const { t, translations } = useStringTranslation();
    const {
        products,
        gridLayout,
        isLoading,
        disableCheckbox,
        selectedProducts,
        activeLanguage,
        onProductSelect,
        onProductClick,
    } = props;

    if (isLoading) {
        return <CategoryProductsSkeleton gridLayout={gridLayout} />;
    }

    if (products.length === 0) {
        return null;
    }

    return (
        <div
            className={
                "c-products-list__sub-item__items " +
                (gridLayout ? "c-products-list__sub-item__items--grid" : "")
            }
        >
            {products.map((product) => {
                const isSelected = isProductSelected(selectedProducts, product);
                const actualDisableCheckbox = disableCheckbox && !isSelected;

                if (gridLayout) {
                    const gridProduct: SimpleProduct =
                        convertProductToGridFormat(
                            product,
                            activeLanguage,
                            translations,
                        );

                    return (
                        <ProductGridItem
                            key={"grid-" + product.id}
                            product={
                                gridProduct as unknown as StorybookSimpleProduct
                            }
                            isSelected={isSelected}
                            disableCheckbox={actualDisableCheckbox}
                            viewProductText={t("view-product")}
                            onProductSelect={(
                                selectedProduct: StorybookSimpleProduct,
                            ) =>
                                onProductSelect(
                                    selectedProduct as unknown as SimpleProduct,
                                )
                            }
                            onProductClick={(
                                selectedProduct: StorybookSimpleProduct,
                            ) =>
                                onProductClick(
                                    selectedProduct as unknown as SimpleProduct,
                                )
                            }
                        />
                    );
                }

                const listProduct: SimpleProduct = convertProductToListFormat(
                    product,
                    activeLanguage,
                );

                return (
                    <ProductListItem
                        key={"list-" + product.id}
                        product={
                            listProduct as unknown as StorybookSimpleProduct
                        }
                        isSelected={isSelected}
                        onProductSelect={(
                            selectedProduct: StorybookSimpleProduct,
                        ) =>
                            onProductSelect(
                                selectedProduct as unknown as SimpleProduct,
                            )
                        }
                        redirectUrl={RouteEnum.PRODUCT_RANGE + `/${product.id}`}
                    />
                );
            })}
        </div>
    );
};
